import { useEffect, useState } from "react";
import { Dialog, Select } from "@rio-cloud/rio-uikit";
import {
  OccurrenceStepEnum,
  OccurrenceStepLabelEnum,
} from "../../../../enums/occurrenceStep";
import { Occurrence, OccurrenceStep } from "../../../../models/occurrence";
import { fieldsLabels } from "./utils/fields-labels";

import ErrorDialog from "../../../../components/dialogs/ErrorDialog";
import ConfirmDialog from "../../../../components/dialogs/ConfirmDialog";
import StepReleaseForm from "./stepReleaseForm";
import { Scheduling } from "../../../../models/schedule";
import { fetchSchedulingByChassisList } from "../../../../api/fetchScheduling";
import TasNumberRequiredDialog, {
  requiresTasBeforeEntering,
} from "../../dialogs/tasNumberRequiredDialog";


interface IChangeStepFormProps {
  reloadOccurrenceList: (occurrenceIdToRemove?: string, occurrenceIdToAdd?: string) => Promise<void>
  step: OccurrenceStep;
  occurrence: Occurrence | null;
  changeStep: (
    service: Occurrence,
    fromStepId: number,
    to: OccurrenceStepEnum
  ) => Promise<Occurrence | null>;
  save: (occurrence: Occurrence) => Promise<Occurrence | false>;
  onClose: () => void;
  requiredFields: string[];
  isValidStep: (requiredFields: string[], occurrence: Occurrence) => boolean;
  hasUnsavedChanges: () => boolean;
  getNotFilledRequiredFields: (
    requiredFields: string[],
    occurrence: Occurrence
  ) => string[];
  finishOccurrence: (occurrence: Occurrence, finalizationReasonType?: string, finalizationReasonDescription?: string, fromStepId?: number) => Promise<boolean>;
  occurrenceStepAvailable: string[];
}

const ChangeStepForm = (props: IChangeStepFormProps) => {
  const stepFound = Object.values(OccurrenceStepLabelEnum).find(
    (s) => s === props.step.name
  );
  const step = props.occurrence?.steps?.find((s) => s.step === props.step.step);
  const [nextStep, setNextStep] = useState<OccurrenceStepEnum | null>(null);
  const [loading, setLoading] = useState(false);
  const [finishLoading, setFinishLoading] = useState(false);
  const [concludeLoading, setConcludeLoading] = useState(false);
  const [scheduling, setScheduling] = useState<Scheduling | undefined>(undefined);
  const [errorDialog, setErrorDialog] = useState({
    show: false,
    title: "",
    body: <></>,
  });

  const [unSavedDialog, setUnSavedDialog] = useState({
    show: false,
    title: "",
    body: "",
  });

  const [releaseFormDialog, setReleaseFormDialog] = useState(false)

  useEffect(() => {
    const fetchScheduling = async () => {
      const occurrence = props.occurrence;

      if (occurrence?.scheduleNumber) {
        const scheduling = (await fetchSchedulingByChassisList([occurrence.vehicleChassis])).find(
          (e) => e.scheduleNumber === occurrence.scheduleNumber,
        );
        setScheduling(scheduling);
      }
    }

    fetchScheduling()
  }, [])
  const [pendingChange, setPendingChange] = useState<{
    fromStepId: number;
    toStep: OccurrenceStepEnum;
  } | null>(null);
  const [tasModalLoading, setTasModalLoading] = useState(false);


  const lastStep =
    props.occurrenceStepAvailable[props.occurrenceStepAvailable.length - 1];
  const isLastStep =
    OccurrenceStepEnum[lastStep as keyof typeof OccurrenceStepEnum] ===
    props.step.step;

  const closeModalWhenOptionIsRelease = () => {
    props.onClose();
    setFinishLoading(false);
    props.reloadOccurrenceList(props.occurrence?.id || '')
  }

  return (
    <div className="w-full">
      <div className="text-size-h4 font-bold">Avançar etapa</div>
      <div className="text-color-status-resting text-size-14 mb-8">
        Selecione a etapa que o item será movido
      </div>

      <div className="w-full border border-sky-600 rounded p-4">
        <div className="text-bold mb-4">
          {stepFound}: {props.occurrence?.vehicleModel}
        </div>
        <hr />
        <div>
          <div className="flex justify-between py-4">
            <div>Início:</div>
            <div>
              {step?.dtStart?.toLocaleString("pt-BR", {
                dateStyle: "short",
                timeStyle: "short",
              })}
            </div>
          </div>
          <div className="flex justify-between py-4">
            <div>Conclusão:</div>
            <div>
              {step?.dtEnd?.toLocaleString("pt-BR", {
                dateStyle: "short",
                timeStyle: "short",
              }) ?? "Não concluído"}
            </div>
          </div>
          <div className="flex justify-between py-4">
            <div>Duração:</div>
            <div>-</div>
          </div>
        </div>
        <div className="p-1 mt-4 border rounded-full text-sky-600 w-full border-sky-300 text-center bg-sky-50">
          Em progresso
        </div>
      </div>

      <div className="flex justify-between w-full gap-6 mt-8">
        <div className="form-group form-group-lg w-full">
          <label htmlFor="nextStep">Selecionar etapa</label>
          <Select
            id="nextStep"
            options={props.occurrenceStepAvailable
              .filter((s) => {
                const stepValue =
                  OccurrenceStepEnum[s as keyof typeof OccurrenceStepEnum];

                // Ocultar a etapa atual
                if (stepValue === props.step.step) return false;

                // Ocultar etapas Deslocamento e Remoção se for ocorrência Preventiva
                if (props.occurrence?.occurrenceType === "P") {
                  return (
                    stepValue !== OccurrenceStepEnum.DISPLACEMENT &&
                    stepValue !== OccurrenceStepEnum.REMOVAL
                  );
                }

                return true;
              })
              .map((s) => ({
                label:
                  OccurrenceStepLabelEnum[
                    s as keyof typeof OccurrenceStepLabelEnum
                  ],
                value: OccurrenceStepEnum[s as keyof typeof OccurrenceStepEnum],
                id: s,
              }))}
            onChange={(e) => setNextStep(e?.value ?? null)}
            placeholder="Selecione a etapa"
            className="w-full"
          />
        </div>
      </div>

      <div className="flex justify-end mt-2 gap-2">
        <button
          className={`btn btn-primary w-[150px] ${
            loading ? "btn-loading disabled" : ""
          }`}
          type="button"
          disabled={!nextStep || loading}
          onClick={async () => {
            if (!props.occurrence || !nextStep || !props.step.step || !props.step.id) return;

            if (props.hasUnsavedChanges()) {
              setUnSavedDialog({
                show: true,
                title: "Atenção",
                body: "Existem alterações não salvas. Deseja continuar sem salvar?",
              });
              return;
            }

            // Add parts validation if to step is PARTS or PARTS_IN_TRANSIT
            const requiredFields = [...props.requiredFields];
            if(nextStep === OccurrenceStepEnum.PARTS) requiredFields.push('partOrders');
            if(nextStep === OccurrenceStepEnum.PARTS_IN_TRANSIT) requiredFields.push('partOrders', 'partOrderParts');

            if (!props.isValidStep(requiredFields, props.occurrence ?? ({} as Occurrence))) {
              setErrorDialog({
                show: true,
                title: "Erro ao alterar etapa",
                body: (
                  <div>
                    <div className="mb-2">
                      É necessário preencher todos os campos obrigatórios para
                      avançar/retroceder de etapa.
                    </div>
                    <div className="mb-2">
                      Campos obrigatórios não preenchidos:
                    </div>
                    <ul className="list-disc list-inside">
                      {props
                        .getNotFilledRequiredFields(
                          requiredFields,
                          props.occurrence
                        )
                        .map((field) => (
                          <li key={field}>
                            {fieldsLabels[field as keyof typeof fieldsLabels]}
                          </li>
                        ))}
                    </ul>
                  </div>
                ),
              });
              return;
            }

            if (requiresTasBeforeEntering(nextStep, props.occurrence)) {
              setPendingChange({ fromStepId: props.step.id, toStep: nextStep });
              return;
            }

            setLoading(true);

            try {
              const newOccurrence = await props.changeStep(
                props.occurrence,
                props.step.id,
                nextStep
              );
              if (!newOccurrence) {
                setLoading(false);
                setErrorDialog({
                  show: true,
                  title: "Erro ao alterar etapa",
                  body: (
                    <div>
                      Verifique os dados e tente novamente ou entre em contato com
                      o suporte.
                    </div>
                  ),
                });
                return;
              }

              props.onClose();
              setLoading(false);
            } catch (error: any) {
              setLoading(false);
              setErrorDialog({
                show: true,
                title: "Erro ao alterar etapa",
                body: <div>{error?.message ?? "Verifique os dados e tente novamente ou entre em contato com o suporte."}</div>,
              });
            }
          }}
        >
          Avançar etapa
        </button>

        {props.step.step === OccurrenceStepEnum.RELEASE && (
          <button
            className={`btn btn-info w-[150px] ${
              finishLoading ? "btn-loading disabled" : ""
            }`}
            type="button"
            disabled={finishLoading}
            onClick={async () => {
              if (!props.occurrence) return;
              if (props.hasUnsavedChanges()) {
                setUnSavedDialog({
                  show: true,
                  title: "Atenção",
                  body: "Existem alterações não salvas. Deseja continuar sem salvar?",
                });
                return;
              }

              const requiredFields = [...props.requiredFields];

              if (!props.isValidStep(requiredFields, props.occurrence ?? ({} as Occurrence))) {
                setErrorDialog({
                  show: true,
                  title: "Erro ao concluir ocorrência",
                  body: (
                    <div>
                      <div className="mb-2">
                        É necessário preencher todos os campos obrigatórios para
                        concluir a ocorrência.
                      </div>
                      <div className="mb-2">
                        Campos obrigatórios não preenchidos:
                      </div>
                      <ul className="list-disc list-inside">
                        {props
                          .getNotFilledRequiredFields(
                            requiredFields,
                            props.occurrence
                          )
                          .map((field) => (
                            <li key={field}>
                              {fieldsLabels[field as keyof typeof fieldsLabels]}
                            </li>
                          ))}
                      </ul>
                    </div>
                  ),
                });
                return;
              }

              setFinishLoading(true);

              let success = true;
              try {
                await props.finishOccurrence(props.occurrence, undefined, undefined, step?.id);
              } catch (error) {
                success = false;
              }
              
              if (!success) {
                setFinishLoading(false);
                setErrorDialog({
                  show: true,
                  title: "Erro ao concluir ocorrência",
                  body: (
                    <div>
                      Verifique os dados e tente novamente ou entre em contato
                      com o suporte.
                    </div>
                  ),
                });
                return;
              }
              
              if(props.occurrence?.volksEssentialsPlanContract) {
                setReleaseFormDialog(true)
                return;
              }

              props.onClose();
              setFinishLoading(false);
              props.reloadOccurrenceList(props.occurrence?.id || '')
            }}
          >
            Concluir ocorrência
          </button>
        )}

        {isLastStep && props.step.step !== OccurrenceStepEnum.RELEASE && (
          <button
            className={`btn btn-info w-[150px] ${
              concludeLoading ? "btn-loading disabled" : ""
            }`}
            type="button"
            disabled={concludeLoading}
            onClick={async () => {
              if (!props.occurrence || !props.step.step || !props.step.id) return;

              if (props.hasUnsavedChanges()) {
                setUnSavedDialog({
                  show: true,
                  title: "Atenção",
                  body: "Existem alterações não salvas. Deseja continuar sem salvar?",
                });
                return;
              }

              // Add parts validation if to step is PARTS or PARTS_IN_TRANSIT
              const nextStepIndex =
                Object.values(OccurrenceStepEnum).indexOf(props.step.step) + 1;
              const _nextStep =
                Object.values(OccurrenceStepEnum)[nextStepIndex];
              const requiredFields = [...props.requiredFields];
              if(_nextStep === OccurrenceStepEnum.PARTS) requiredFields.push('partOrders');
              if(_nextStep === OccurrenceStepEnum.PARTS_IN_TRANSIT) requiredFields.push('partOrders', 'partOrderParts');

              if (!props.isValidStep(requiredFields, props.occurrence ?? ({} as Occurrence))) {
                setErrorDialog({
                  show: true,
                  title: "Erro ao concluir etapa",
                  body: (
                    <div>
                      <div className="mb-2">
                        É necessário preencher todos os campos obrigatórios para
                        concluir a etapa.
                      </div>
                      <div className="mb-2">
                        Campos obrigatórios não preenchidos:
                      </div>
                      <ul className="list-disc list-inside">
                        {props
                          .getNotFilledRequiredFields(
                            requiredFields,
                            props.occurrence
                          )
                          .map((field) => (
                            <li key={field}>
                              {fieldsLabels[field as keyof typeof fieldsLabels]}
                            </li>
                          ))}
                      </ul>
                    </div>
                  ),
                });
                return;
              }

              if (requiresTasBeforeEntering(_nextStep, props.occurrence)) {
                setPendingChange({ fromStepId: props.step.id, toStep: _nextStep });
                return;
              }

              setConcludeLoading(true);
              const newOccurrence = await props.changeStep(
                props.occurrence,
                props.step.id,
                _nextStep
              );
              if (!newOccurrence) {
                setConcludeLoading(false);
                setErrorDialog({
                  show: true,
                  title: "Erro ao concluir etapa",
                  body: (
                    <div>
                      Verifique os dados e tente novamente ou entre em contato
                      com o suporte.
                    </div>
                  ),
                });
                return;
              }

              props.onClose();
              setConcludeLoading(false);
            }}
          >
            Concluir etapa
          </button>
        )}
      </div>

      <ErrorDialog
        show={errorDialog.show}
        title={errorDialog.title}
        body={errorDialog.body}
        onClose={() => setErrorDialog({ show: false, title: "", body: <></> })}
      />

      <Dialog
          show={releaseFormDialog}
          title={'Liberação'}
          body={
            <StepReleaseForm 
              scheduling={scheduling} 
              occurrence={props.occurrence} 
              closeFirstModal={closeModalWhenOptionIsRelease}
              setReleaseFormDialog={setReleaseFormDialog}
            />
          }
          showCloseButton={false}
          className='exampleDialog'
          bsSize='md'
      />

      <ConfirmDialog
        show={unSavedDialog.show}
        title={unSavedDialog.title}
        body={unSavedDialog.body}
        onClose={() => setUnSavedDialog({ show: false, title: "", body: "" })}
        onConfirm={async () => {
          if (!props.occurrence || !props.step.step || !props.step.id) return;

          if (props.step.step !== OccurrenceStepEnum.RELEASE && !nextStep)
            return;

          // Add parts validation if to step is PARTS or PARTS_IN_TRANSIT
          const requiredFields = [...props.requiredFields];
          if(nextStep === OccurrenceStepEnum.PARTS) requiredFields.push('partOrders');
          if(nextStep === OccurrenceStepEnum.PARTS_IN_TRANSIT) requiredFields.push('partOrders', 'partOrderParts');

          if (!props.isValidStep(requiredFields, props.occurrence ?? ({} as Occurrence))) {
            setErrorDialog({
              show: true,
              title: "Erro ao alterar etapa",
              body: (
                <div>
                  <div className="mb-2">
                    É necessário preencher todos os campos obrigatórios para
                    avançar/retroceder de etapa.
                  </div>
                  <div className="mb-2">
                    Campos obrigatórios não preenchidos:
                  </div>
                  <ul className="list-disc list-inside">
                    {props
                      .getNotFilledRequiredFields(
                        requiredFields,
                        props.occurrence
                      )
                      .map((field) => (
                        <li key={field}>
                          {fieldsLabels[field as keyof typeof fieldsLabels]}
                        </li>
                      ))}
                  </ul>
                </div>
              ),
            });
            return;
          }

          if (
            props.step.step !== OccurrenceStepEnum.RELEASE &&
            requiresTasBeforeEntering(nextStep, props.occurrence)
          ) {
            setUnSavedDialog({ show: false, title: "", body: "" });
            setPendingChange({ fromStepId: props.step.id, toStep: nextStep! });
            return;
          }

          if (props.step.step !== OccurrenceStepEnum.RELEASE) {
            setLoading(true);

            const newOccurrence = await props.changeStep(
              props.occurrence,
              props.step.id,
              nextStep ?? OccurrenceStepEnum.CALL
            );
            if (!newOccurrence) {
              setLoading(false);
              setErrorDialog({
                show: true,
                title: "Erro ao alterar etapa",
                body: (
                  <div>
                    Verifique os dados e tente novamente ou entre em contato com
                    o suporte.
                  </div>
                ),
              });
              return;
            }

            props.onClose();
            setLoading(false);
          }

          if (props.step.step === OccurrenceStepEnum.RELEASE) {
            setFinishLoading(true);

            const newOccurrence = await props.finishOccurrence(
              props.occurrence, undefined, undefined, step?.id
            );
            if (!newOccurrence) {
              setFinishLoading(false);
              setErrorDialog({
                show: true,
                title: "Erro ao concluir ocorrência",
                body: (
                  <div>
                    Verifique os dados e tente novamente ou entre em contato com
                    o suporte.
                  </div>
                ),
              });
              return;
            }

            props.onClose();
            setFinishLoading(false);
          }
        }}
      />

      <TasNumberRequiredDialog
        show={!!pendingChange}
        occurrence={props.occurrence}
        loading={tasModalLoading}
        onCancel={() => setPendingChange(null)}
        onConfirm={async (tasNumber) => {
          if (!props.occurrence || !pendingChange) return;

          setTasModalLoading(true);
          try {
            const saved = await props.save({ ...props.occurrence, tasNumber });
            if (!saved) {
              setTasModalLoading(false);
              setErrorDialog({
                show: true,
                title: "Erro ao salvar número TAS",
                body: (
                  <div>
                    Verifique os dados e tente novamente ou entre em contato com
                    o suporte.
                  </div>
                ),
              });
              return;
            }

            const newOccurrence = await props.changeStep(
              { ...props.occurrence, tasNumber },
              pendingChange.fromStepId,
              pendingChange.toStep
            );
            if (!newOccurrence) {
              setTasModalLoading(false);
              setErrorDialog({
                show: true,
                title: "Erro ao alterar etapa",
                body: (
                  <div>
                    Verifique os dados e tente novamente ou entre em contato com
                    o suporte.
                  </div>
                ),
              });
              return;
            }

            setTasModalLoading(false);
            setPendingChange(null);
            props.onClose();
          } catch (error: any) {
            setTasModalLoading(false);
            setErrorDialog({
              show: true,
              title: "Erro ao alterar etapa",
              body: <div>{error?.message ?? "Verifique os dados e tente novamente ou entre em contato com o suporte."}</div>,
            });
          }
        }}
      />

    </div>
  );
};

export default ChangeStepForm;
